import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import { AppLayout } from "@/components/ui-system";
import EdgeAIAssistant from "@/components/EdgeAIAssistant";
import { can } from "@/lib/permissions";
import {
  Store, Users, MapPin, MapPinned, UserCircle2, Package, Calendar, Truck, Activity,
  TrendingUp, Upload, Bell, RefreshCw, Flag, FileSpreadsheet, BookOpen, Calculator,
  Navigation, ArrowLeftRight, FileText, ShieldCheck, PackageCheck, Layers, Globe,
  PackageSearch, BadgeCheck, Sparkles, RotateCcw, Banknote, PackageX, LifeBuoy, Scale, Zap,
  MessageCircle, PackagePlus, Webhook,
} from "lucide-react";

const AdminLayout = ({ children, user, onLogout }) => {
  const navigate = useNavigate();
  const [redFlagCount, setRedFlagCount] = useState({ total: 0, high_priority: 0 });
  const [supportOpen, setSupportOpen] = useState(0);
  const [pendingPickups, setPendingPickups] = useState(0);

  const canViewRedFlags = can(user, "red_flags:view");
  const canViewSupport = can(user, "support:view");
  const canViewPickups = can(user, "pickup_requests:view");
  useEffect(() => {
    if (!canViewRedFlags) return;
    const fetchRedFlagCount = async () => {
      try {
        const res = await axios.get(`${API}/red-flags/count`);
        setRedFlagCount(res.data);
      } catch {
        console.error("Failed to fetch red flag count");
      }
    };
    fetchRedFlagCount();
    const interval = setInterval(fetchRedFlagCount, 120000);
    return () => clearInterval(interval);
  }, [canViewRedFlags]);

  useEffect(() => {
    if (!canViewSupport) return;
    const fetchOpen = () => axios.get(`${API}/admin/support/open-count`).then((r) => setSupportOpen(r.data.open ?? 0)).catch(() => {});
    fetchOpen();
    const interval = setInterval(fetchOpen, 120000);
    return () => clearInterval(interval);
  }, [canViewSupport]);

  // Pickup Requests badge — how many requests are still waiting to be scheduled with
  // the carrier. Polled, not pushed: this app has no websocket/SSE anywhere, and the
  // endpoint is a single count over a partial index. 60s rather than the 120s above
  // because a pickup is same-day work. Three triggers, so the dot never lies:
  //   - the interval, for requests a seller raises while the admin sits on a page
  //   - tab focus, so a backgrounded tab is correct the instant it is looked at
  //   - the "pickup-requests:changed" event, so scheduling one clears the badge at
  //     once instead of leaving it blinking for up to a minute (fired by
  //     AdminPickupRequests after schedule / cancel / create)
  useEffect(() => {
    if (!canViewPickups) return;
    const controller = new AbortController();
    const fetchPending = async () => {
      try {
        const res = await axios.get(`${API}/pickup-requests/pending-count`, { signal: controller.signal });
        setPendingPickups(res.data.pending ?? 0);
      } catch {
        // Keep the last known count. Clearing it on a transient network blip would
        // hide pickups that are still genuinely waiting, which is the one failure
        // mode this indicator must not have.
      }
    };
    fetchPending();
    const interval = setInterval(fetchPending, 60000);
    const onVisible = () => { if (!document.hidden) fetchPending(); };
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("pickup-requests:changed", fetchPending);
    return () => {
      controller.abort();
      clearInterval(interval);
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("pickup-requests:changed", fetchPending);
    };
  }, [canViewPickups]);

  // Permission keys per docs/RBAC_MIGRATION_MAP.md "Frontend contract".
  // Gating here is cosmetic — the backend enforces the same permissions.
  // Grouped into sections so B2B Last Mile and B2C Last Mile are visibly
  // separate and each settings/details area is easy to find. Section rows
  // ({ section }) are rendered as group labels and pruned when empty.
  const allNavItems = [
    { section: "Overview" },
    { name: "Ops Red Flags", path: "/admin/red-flags", icon: Flag, testId: "nav-red-flags", badge: redFlagCount.total, permission: "red_flags:view" },
    { name: "Commercial Red Flags", path: "/admin/commercial-red-flags", icon: Banknote, testId: "nav-commercial-red-flags", permission: "red_flags:view" },
    { name: "Rate Errors", path: "/admin/rate-errors", icon: Scale, testId: "nav-rate-errors", permission: "bulk_operations:view" },

    { section: "B2C Last Mile" },
    { name: "B2C Dashboard", path: "/admin/b2c", icon: Store, testId: "nav-b2c-dashboard", end: true, permission: "b2c.orders:view" },
    { name: "B2C Orders", path: "/admin/b2c/orders", icon: PackageSearch, testId: "nav-b2c-orders", permission: "b2c.orders:view" },
    { name: "Create B2C Order", path: "/admin/b2c/orders/create", icon: PackagePlus, testId: "nav-b2c-create-order", permission: "b2c.orders:create" },
    { name: "Returns & Exchange", path: "/admin/b2c/returns", icon: RotateCcw, testId: "nav-b2c-returns", permission: "b2c.returns:view" },
    { name: "COD Reconciliation", path: "/admin/b2c/cod", icon: Banknote, testId: "nav-b2c-cod", permission: "b2c.cod:view" },
    { name: "Early COD", path: "/admin/b2c/early-cod", icon: Zap, testId: "nav-b2c-early-cod", permission: "b2c.early_cod:manage" },
    { name: "NDR", path: "/admin/b2c/ndr", icon: PackageX, testId: "nav-b2c-ndr", permission: "b2c.ndr:view" },
    { name: "Weight Discrepancy", path: "/admin/b2c/weight-discrepancies", icon: Scale, testId: "nav-b2c-weight", permission: "b2c.weight_discrepancy:view" },
    { name: "B2C Partners", path: "/admin/b2c/partners", icon: PackageCheck, testId: "nav-b2c-partners", permission: "b2c.partners:manage" },
    { name: "B2C Recommendation", path: "/admin/b2c/recommendation", icon: Sparkles, testId: "nav-b2c-recommendation", permission: "b2c.recommendation:manage" },
    { name: "B2C Plans & Rates", path: "/admin/b2c/plans", icon: Layers, testId: "nav-b2c-plans", permission: "b2c.plans:view" },
    { name: "B2C Zones", path: "/admin/b2c/zones", icon: Globe, testId: "nav-b2c-zones", permission: "b2c.zones:manage" },
    { name: "B2C KYC", path: "/admin/b2c/kyc", icon: BadgeCheck, testId: "nav-b2c-kyc", permission: "b2c.kyc:review" },
    { name: "Tracking Webhooks", path: "/admin/b2c/webhooks", icon: Webhook, testId: "nav-b2c-webhooks", permission: "b2c.orders:view" },

    { section: "B2B Last Mile" },
    { name: "B2B Dashboard", path: "/admin", icon: Store, testId: "nav-dashboard", end: true, permission: "dashboard:view" },
    { name: "B2B Orders", path: "/admin/orders", icon: Package, testId: "nav-orders", permission: "orders:view" },
    { name: "Bulk Orders", path: "/admin/bulk-orders", icon: Upload, testId: "nav-bulk-orders", permission: "bulk_orders:view" },
    { name: "Bulk Operations", path: "/admin/bulk-operations", icon: RefreshCw, testId: "nav-bulk-operations", permission: "bulk_operations:view" },
    { name: "Appointments", path: "/admin/appointments", icon: Calendar, testId: "nav-appointments", permission: "appointments:view" },
    { name: "Pickup Requests", path: "/admin/pickup-requests", icon: Truck, testId: "nav-pickup-requests", badge: pendingPickups, pulse: true, permission: "pickup_requests:view" },
    { name: "B2B Marketplaces", path: "/admin/b2b-marketplace", icon: Store, testId: "nav-b2b-marketplace", permission: "b2b_marketplace:view" },
    { name: "B2B Shipping Partners", path: "/admin/b2b-shipping-partner", icon: Truck, testId: "nav-b2b-shipping-partner", permission: "b2b_partners:manage" },

    { section: "Tools" },
    { name: "TAT Calculator", path: "/admin/tat-calculator", icon: Calculator, testId: "nav-tat-calculator", permission: "tools:use" },
    { name: "Freight Estimator", path: "/admin/freight-estimator", icon: Package, testId: "nav-freight-estimator", permission: "tools:use" },
    { name: "Route Planning", path: "/admin/route-planning", icon: Navigation, testId: "nav-route-planning", permission: "tools:use" },

    { section: "Finance" },
    { name: "P&L Report", path: "/admin/pnl-report", icon: TrendingUp, testId: "nav-pnl-report", permission: "pnl:view" },
    { name: "Billing", path: "/admin/billing", icon: FileText, testId: "nav-billing", permission: "billing:view" },

    { section: "Locations" },
    { name: "Pickup Locations", path: "/admin/pickup-locations", icon: MapPin, testId: "nav-pickup-locations", permission: "locations:view" },
    { name: "Drop Locations", path: "/admin/drop-locations", icon: MapPinned, testId: "nav-drop-locations", permission: "locations:view" },

    { section: "Sellers & Access" },
    { name: "Sellers", path: "/admin/sellers", icon: Users, testId: "nav-sellers", permission: "sellers:view" },
    { name: "Support", path: "/admin/support", icon: LifeBuoy, testId: "nav-support", permission: "support:view" },
    { name: "Users", path: "/admin/users", icon: UserCircle2, testId: "nav-users", permission: "users:view" },
    { name: "Roles & Permissions", path: "/admin/roles", icon: ShieldCheck, testId: "nav-roles", adminOnly: true },

    { section: "Reports & Logs" },
    { name: "Reports", path: "/admin/reports", icon: FileSpreadsheet, testId: "nav-reports", permission: "reports:view" },
    { name: "Notification Setup", path: "/admin/notification-setup", icon: Bell, testId: "nav-notification-setup", permission: "notifications:view" },
    { name: "WhatsApp Notifications", path: "/admin/whatsapp-notification-setup", icon: MessageCircle, testId: "nav-whatsapp-notification-setup", permission: "notifications:view" },
    { name: "API Logs", path: "/admin/api-logs", icon: Activity, testId: "nav-api-logs", permission: "api_logs:view" },

    { section: "Developer Docs" },
    { name: "Shopify Integration", path: "/admin/dev-docs/shopify", icon: BookOpen, testId: "nav-devdocs-shopify", permission: "developer_docs:view" },
    { name: "WooCommerce Integration", path: "/admin/dev-docs/woocommerce", icon: BookOpen, testId: "nav-devdocs-woocommerce", permission: "developer_docs:view" },
    { name: "Magento Integration", path: "/admin/dev-docs/magento", icon: BookOpen, testId: "nav-devdocs-magento", permission: "developer_docs:view" },
    { name: "Custom / API Integration", path: "/admin/dev-docs/custom-api", icon: BookOpen, testId: "nav-devdocs-custom", permission: "developer_docs:view" },
  ];

  // Filter by permission (Roles & Permissions is hard-locked to `admin`), then
  // drop any section header left with no visible item under it.
  const visibleItems = allNavItems.filter((item) =>
    item.section
      ? true
      : item.adminOnly
      ? user?.role === "admin"
      : can(user, item.permission)
  );
  const nav = visibleItems.filter((item, idx) =>
    !item.section || (visibleItems[idx + 1] && !visibleItems[idx + 1].section)
  );

  const topBar = (
    <div className="hidden md:flex items-center justify-between px-8 py-2 bg-white border-b border-slate-100">
      <button
        onClick={() => navigate("/pharmeasy")}
        className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-emerald-700 bg-emerald-50 hover:bg-emerald-100 rounded-md transition-colors"
        data-testid="switch-to-pharmeasy"
      >
        <ArrowLeftRight className="w-4 h-4" /> Switch to Pharmeasy Ops
      </button>
      <div className="flex items-center gap-2">
        {can(user, "support:view") && (
          <button
            onClick={() => navigate("/admin/support")}
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-semibold text-teal-700 bg-teal-50 hover:bg-teal-100 rounded-md transition-colors"
            data-testid="support-top-btn"
            title="Support — manage seller tickets"
          >
            <LifeBuoy className="w-4 h-4" /> Support
            {supportOpen > 0 && (
              <span className="ml-0.5 min-w-[18px] h-[18px] px-1 inline-flex items-center justify-center rounded-full bg-teal-600 text-white text-xs font-bold" data-testid="support-open-count">
                {supportOpen > 99 ? "99+" : supportOpen}
              </span>
            )}
          </button>
        )}
        <button
          onClick={() => navigate("/admin/docs")}
          className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-slate-600 hover:text-teal-600 hover:bg-teal-50 rounded-md transition-colors"
          data-testid="docs-top-btn"
        >
          <BookOpen className="w-4 h-4" /> Documentation
        </button>
      </div>
    </div>
  );

  const drawerExtra = (
    <button
      onClick={() => navigate("/pharmeasy")}
      className="w-full mb-3 flex items-center gap-3 px-4 py-3 bg-emerald-50 text-emerald-700 rounded-lg hover:bg-emerald-100 transition-colors"
      data-testid="mobile-switch-to-pharmeasy"
    >
      <ArrowLeftRight className="w-5 h-5" />
      <span className="text-sm font-medium">Switch to Pharmeasy Ops</span>
    </button>
  );

  return (
    <AppLayout
      variant="sidebar"
      accent="teal"
      navKey="admin"
      brand={{ name: "EdgeLMS", subtitle: "Admin Portal" }}
      nav={nav}
      user={user}
      onLogout={onLogout}
      profilePath="/admin/profile"
      topBar={topBar}
      drawerExtra={drawerExtra}
      assistant={<EdgeAIAssistant />}
    >
      {children}
    </AppLayout>
  );
};

export default AdminLayout;
